import React, { useState } from 'react';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useModuleSlides, useUpdateSlide, AdminModuleSlide } from '@/hooks/useEducationAdmin';
import { SlideCard } from './components/SlideCard';
import { SlideEditDialog } from './SlideEditDialog';

interface SlideManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  moduleId: string;
}

export const SlideManager: React.FC<SlideManagerProps> = ({
  open,
  onOpenChange,
  moduleId,
}) => {
  const { data: slides = [], isLoading } = useModuleSlides(moduleId);
  const updateSlide = useUpdateSlide();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSlide, setEditingSlide] = useState<AdminModuleSlide | null>(null);
  const [localSlides, setLocalSlides] = useState<AdminModuleSlide[]>([]);

  React.useEffect(() => {
    setLocalSlides(slides);
  }, [slides]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localSlides.findIndex((item) => item.id === active.id);
      const newIndex = localSlides.findIndex((item) => item.id === over.id);
      
      const newOrder = arrayMove(localSlides, oldIndex, newIndex);
      setLocalSlides(newOrder);

      // Update sort_order for all affected items
      const updates = newOrder.map((slide, index) => 
        updateSlide.mutateAsync({ 
          id: slide.id, 
          sort_order: index 
        })
      );

      try {
        await Promise.all(updates);
      } catch (error) {
        // Revert on error
        setLocalSlides(slides);
      }
    }
  };

  const handleCreate = () => {
    setEditingSlide(null);
    setEditDialogOpen(true);
  };

  const handleEdit = (slide: AdminModuleSlide) => {
    setEditingSlide(slide);
    setEditDialogOpen(true);
  };

  if (!moduleId) {
    return null;
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Manage Slides</DialogTitle>
            <DialogDescription>
              Create and organize slides for this module. Drag and drop to reorder.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                {localSlides.length} slide(s)
              </div>
              <Button onClick={handleCreate} size="sm">
                <Plus className="mr-2 h-4 w-4" />
                Add Slide
              </Button>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            ) : localSlides.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No slides found. Create your first slide to get started.</p>
              </div>
            ) : (
              <div className="max-h-[500px] overflow-y-auto space-y-2">
                <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={localSlides.map(s => s.id)} strategy={verticalListSortingStrategy}>
                    {localSlides.map((slide) => (
                      <SlideCard
                        key={slide.id}
                        slide={slide}
                        onEdit={handleEdit}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <SlideEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        slide={editingSlide}
        moduleId={moduleId}
      />
    </>
  );
};